/* eslint-disable import/prefer-default-export */
import { Attributes } from '@leverege/ui-attributes'
import { ValuesCache } from '@leverege/value-cache'
import Unit from '@leverege/unit'
import { DateTime, Interval } from 'luxon'
import Constants from './Constants'

const {
  ONE_HOUR,
  ANDON_COLORS,
  BAY_TIME_ANDON_DEFAULTS
} = Constants

const cache = new ValuesCache()

const validRangeForGhostEvent = [
  'last24Hours',
  'today',
  'last7Days',
  'last30Days',
]

function roundToNDecimals( num, n ) {
  const factor = 10 ** n
  return Math.round( num * factor ) / factor
}

function addGhostEventForBinObj( o, rangeStartMS, rangeEndMS, occupancyEvents ) {
  if ( o.events.length !== 0 ) return
    
  const { deviceId, obj, obj : { data : { currentStage, currentStageStartedAt } } } = o

  const nowMS = Date.now()
  const endMS = nowMS // rangeEndMS using NOW cuz of the validRangeForGhostEvent check guarantees us able to use it

  const startMs = new Date( currentStageStartedAt ).getTime()
  const durationMS = endMS - startMs
  // const deviceId = value.obj.id
    
  const averageWorkerCount = calculateAvgWorkerCountForStage( {
    deviceId,
    occupancyEvents,
    startMs,
    stopMs : rangeEndMS
  } )
  
  const theOnlyMoldEvent = {
    id : `madeUpId-${deviceId}`,
    type : 'moldStage',
    startTime : startMs,
    stopTime : endMS,
    durationMS,
    stage : currentStage,
    obj,
    averageWorkerCount,
  }
  
  o.events = [ theOnlyMoldEvent ]  
}

function calculateAvgWorkerCountForStage( { deviceId, occupancyEvents, startMs, stopMs } ) {

  let nMatches = 0
  const sum = occupancyEvents.reduce( ( accumulator, evt ) => {
    const halves = evt?.halves
    if ( halves == null ) return accumulator
  
    const ts = new Date( evt.hour ).getTime()
    const inTimeRange = startMs <= ts && ts < stopMs
    if ( !inTimeRange ) return accumulator
  
    const halfIds = Object.keys( evt.halves )
    if ( !halfIds.includes( deviceId ) ) return accumulator
  
    const count = evt.halves[deviceId].count
    nMatches++
    return accumulator + count
  }, 0 )
  const averageWorkerCount = roundToNDecimals( sum / nMatches, 2 )
  return Number.isNaN( averageWorkerCount ) ? '-' : averageWorkerCount
}

export const getItems = ( events, items, occupancyEvents, { timezone, queryType, timeRange : { rangeStartMS, rangeEndMS, rangeString } } ) => {

  // hItems = events
  // items = halves (not lines, since we're doing it more granular a la pedro)
  // function takes events that componennt gets and creates event pairs
  // imagine events (one when a era starts and one whne an era ends)
  // this turns the event list into a list of super events with start and end as opposed to just start

  const allowedDeviceIds = items.map( o => o.id )
  const filteredEvents = events.filter( ( item ) => {
    const { deviceId } = item
    return allowedDeviceIds.includes( deviceId )
  } )

  return cache.calc( 'items', ( ) => {
    // let minTime, maxTime
    const eventTimes = events.map( ( event ) => {
      const date = new Date( event.data.time )
      return date.toISOString()
    } )

    const eventTimestamps = eventTimes.map( time => new Date( time ).getTime() )
        
    const maxTime = Math.max( ...eventTimestamps )
    const minTime = Math.min( ...eventTimestamps )

    const bins = new Map()
    if ( filteredEvents == null ) {
      return { bins }
    }
    items.forEach( ( item ) => {
      bins.set( item.id, { deviceId : item.id, samples : [], obj : item } )
    } )

    for ( let n = 0; n < filteredEvents.length; n++ ) {
      const event = filteredEvents[n]
      const deviceId = event.deviceId

      const bin = bins.get( deviceId )
      if ( bin == null ) {

        const obj = items.find( item => item.id === deviceId )
        bins.set( deviceId, { deviceId, samples : [ event ], obj } )
      } else {
        bin.samples.push( event )
      }
    }

    bins.forEach( ( value, key, map ) => {
      if ( !value?.obj?.data ) {
        return
      }

      const { samples } = value
      const events = []

      for ( let n = 0; n < samples.length; n++ ) { // turn sample and next sample into an event
        if ( !samples[n] ) continue
        const { 
          id : curSampleId,
          data : { 
            time : startTime,
            data : dataWithTheStage
          }
        } = samples[n]

        const stage = dataWithTheStage?.stage ?? dataWithTheStage?.status

        const hasAnother = ( n + 1 ) < samples.length
        const nextSampleId = hasAnother ? samples?.[n + 1]?.id : 'open'
        
        const stopTime = hasAnother ? samples?.[n + 1]?.data?.time : Date.now()
        const startMs = new Date( startTime ).getTime() // convert to timestamp
        const stopMs = new Date( stopTime ).getTime() // convert to timestamp

        const moldEventId = `${curSampleId}-${nextSampleId}`

        const duration = stopMs - startMs
        const deviceId = value.obj.id
        
        const averageWorkerCount = calculateAvgWorkerCountForStage( {
          deviceId, occupancyEvents, startMs, stopMs
        } )

        const moldEvent = {
          id : moldEventId,
          type : 'moldStage',
          startTime : startMs,
          stopTime : stopMs,
          duration,
          stage,
          obj : value.obj,
          averageWorkerCount,
        }

        if ( moldEvent.stopTime > rangeStartMS && moldEvent.startTime < rangeStartMS ) {
          // mold overlaps the start of the range
          moldEvent.startTime = rangeStartMS
          events.push( moldEvent )
        } else if ( moldEvent.stopTime >= rangeStartMS ) {
          // this prevents pushing events that wouldn't show in the gantt chart anyway
          events.push( moldEvent )
        }        
      }

      value.events = events
    } )

    bins.forEach( ( o ) => {
      if ( o.events.length === 0 && validRangeForGhostEvent.includes( rangeString ) ) {
        addGhostEventForBinObj( o, rangeStartMS, rangeEndMS, occupancyEvents )
      } 
    } )

    return { minTime, maxTime, bins }
  }, filteredEvents, items )
}

export const getUtilizationStats = ( data, businessDays, type, selectedBay, timezone ) => {
  let bayData

  if ( selectedBay ) {
    bayData = [ data.bins.get( selectedBay ) ]
  } else {
    bayData = Array.from( data.bins.values() )
  }
  const stats = bayData.reduce( ( prev, curr ) => {
    if ( !curr?.events ) return prev
    let lastEvent = null
    curr.events.forEach( ( event, index ) => {
      if ( event.type === type ) {
        const prevEvent = curr.events[index - 1]
        const eventStartDateTime = DateTime.fromMillis( event.startTime )
        const eventEndDateTime = DateTime.fromMillis( event.stopTime )

        if ( ( eventStartDateTime.day !== eventEndDateTime.day ) ) {
          return
        }

        if ( prevEvent ) {
          const prevEventEndDateTime = DateTime.fromMillis( prevEvent.stopTime )

          if ( ( eventStartDateTime.day === prevEventEndDateTime.day ) ) {
            if ( event.leadTime ) {
              prev.eventsWithLeadTime += 1
              prev.totalLeadTime += event.leadTime
            }
          }
        }

        prev.services.push( event )
        prev.totalOccupied += event.duration

        if ( lastEvent == null ) {
          lastEvent = event
        } else {
          const lastEventDateTime = DateTime.fromMillis( lastEvent.stopTime, { zone : timezone } )

          if ( ( lastEventDateTime.day !== eventEndDateTime.day ) ) {
            lastEvent = event
            return
          }

          prev.totalUnoccupied += event.startTime - lastEvent.stopTime

          lastEvent = event
        }
      }
    } )

    return prev
  }, { services : [], totalOccupied : 0, totalUnoccupied : 0, totalLeadTime : 0, eventsWithLeadTime : 0 } )

  let totalBusinessHours = 0

  if ( businessDays.length > 0 ) {
    const now = Date.now()
    businessDays.forEach( ( [ startTS, endTS ] ) => {
      totalBusinessHours += Interval.fromDateTimes(
        DateTime.fromMillis( startTS > now ? now : startTS ),
        DateTime.fromMillis( endTS > now ? now : endTS )
      ).length( 'milliseconds' )
    } )
  }

  const totalUnoccupied = stats.totalUnoccupied // totalBusinessHours - stats.totalOccupied

  return {
    ...stats,
    averageServiceLength : stats.totalOccupied / ( stats.services.length || 1 ),
    totalBusinessHours,
    totalUnoccupied,
    percentOccupied : Math.round( ( stats.totalOccupied / ( stats.totalOccupied + totalUnoccupied ) ) * 100 ),
    percentUnoccupied : Math.round( ( totalUnoccupied / ( stats.totalOccupied + totalUnoccupied ) ) * 100 ),
    averageOccupied : stats.totalOccupied / ( businessDays.length || 1 ),
    averageUnoccupied : totalUnoccupied / ( businessDays.length || 1 )
  }
}

export const getRowsByType = ( rows, type ) => {
  return cache.calc( `rows-${type}`, ( rows, type ) => {
    return rows.map( ( row ) => {
      if ( !row ) return []
      return row.filter( ( item ) => {
        return item.type === type
      } )
    } )
  }, rows, type )
}

export function isDuringBusinessHours( data, businessDays ) {
  let { startTime, stopTime } = data
  const { type, id } = data

  if ( type === 'carOnLift' ) {
    const [ startId, endId ] = id.split( '-' )

    startTime -= ( 30 + ( 1000 * offsetSeconds( 60, startId ) ) )
    stopTime += ( 30 + ( 1000 * offsetSeconds( 60, endId ) ) )
  }

  return businessDays.some( ( day ) => {
    const [ start, end ] = day

    return startTime >= ( start - ONE_HOUR ) && stopTime <= ( end + ONE_HOUR )
  } )
}

function hashCode( str ) {
  let hash = 0
  if ( str == null || str.length === 0 ) {
    return hash
  }
  for ( let i = 0; i < str.length; i++ ) {
    const chr = str.charCodeAt( i )
    hash = ( ( hash << 5 ) - hash ) + chr
    hash |= 0 // Convert to 32bit integer
  }
  return Math.abs( hash ) // make it positive
}

export function offsetSeconds( number, key ) {
  return hashCode( key ) % number
}

export function getBayAndon( bay, serviceDuration ) {
  const yellowOverride = Attributes.get( 'bay.config.bayTimeTarget.yellow', bay )
  const redOverride = Attributes.get( 'bay.config.bayTimeTarget.red', bay )

  if ( redOverride || yellowOverride ) {
    if ( redOverride ) {
      const ms = Unit.convert( redOverride.value, redOverride.unit, 'ms' )

      if ( serviceDuration >= ms ) {
        return ANDON_COLORS.red
      }
    }

    if ( yellowOverride ) {
      const ms = Unit.convert( yellowOverride.value, yellowOverride.unit, 'ms' )

      if ( serviceDuration >= ms ) {
        return ANDON_COLORS.yellow
      }
    }
    return ANDON_COLORS.green
  }

  if ( serviceDuration > BAY_TIME_ANDON_DEFAULTS.critical ) {
    return ANDON_COLORS.red
  } if ( serviceDuration > BAY_TIME_ANDON_DEFAULTS.warn ) {
    return ANDON_COLORS.yellow
  }

  return ANDON_COLORS.green
}
